import {
  canShowOrUpsell,
  hasNoFeatureAccess,
} from '@utils/featureAccess'
import { treasuryVisible } from '@constants/navigation-routes'
import { canShowBackOfficeRoute } from '@modules/back-office/utils/permissions/permissions'

// Marcadores de roteamento dinâmico (destino resolvido no clique). Definidos aqui
// (e não em navigation-modules) para evitar ciclo de import — navigation-modules
// já importa routeModuleKey deste arquivo.
export const DYNAMIC_ROUTE = {
  COMISSOES: 'comissoes',
  TREASURY: 'treasury',
  CONSOLIDADOR: 'consolidador',
}

// Marcadores de gate de visibilidade especial (além de flag simples).
export const VISIBILITY_GATE = {
  TREASURY_SUBROUTES: 'treasury-subroutes',
  BACK_OFFICE_ACCESS: 'back-office-access',
}

/**
 * Chave do módulo de uma rota = primeiro segmento do name (antes do primeiro
 * ponto). Ex: 'comissoes.visao-geral.overview' -> 'comissoes', 'carteira' ->
 * 'carteira'. Usada para casar item ↔ rota mesmo após redirects internos.
 * @param {string} routeName
 * @returns {string}
 */
export function routeModuleKey(routeName) {
  if (!routeName) return ''
  return routeName.split('.')[0]
}

/**
 * Decide se um item é visível e se entra em estado de upsell (acinzentado),
 * espelhando VERBATIM a nav antiga:
 *   - upsellFlag (CRM/Compliance/Drive): canShowOrUpsell decide visibilidade;
 *     noAccessIcon = hasNoFeatureAccess (item aparece cinza p/ quem não tem acesso).
 *   - visibilityGate TREASURY_SUBROUTES: visível se há subrota real (treasuryVisible).
 *   - visibilityGate BACK_OFFICE_ACCESS: visível só se canShowBackOfficeRoute
 *     (IS_INTERNAL OU BACK_OFFICE_SELF_SERVICE, E a flag BACK_OFFICE) — flag
 *     simples sozinha mostraria o item pra quem não pode de fato operar,
 *     quebrando ao clicar (mesmo guard usado na rota real do módulo).
 *   - flag simples: visível se tem a flag.
 *   - internalOnly: só interno.
 * @returns {{ visible: boolean, noAccessIcon: boolean }}
 */
function evaluateItem(item, { show, isInternal, currentUser }) {
  if (item.internalOnly && !isInternal) {
    return { visible: false, noAccessIcon: false }
  }
  if (item.upsellFlag) {
    return {
      visible: canShowOrUpsell(show, item.upsellFlag),
      noAccessIcon: hasNoFeatureAccess(show, item.upsellFlag),
    }
  }
  if (item.visibilityGate === VISIBILITY_GATE.TREASURY_SUBROUTES) {
    return { visible: treasuryVisible(currentUser, show), noAccessIcon: false }
  }
  if (item.visibilityGate === VISIBILITY_GATE.BACK_OFFICE_ACCESS) {
    return {
      visible: canShowBackOfficeRoute({ user: currentUser, access: show }),
      noAccessIcon: false,
    }
  }
  const visible = !item.flag || !!show[item.flag]
  return { visible, noAccessIcon: false }
}

/**
 * Filtra os grupos de módulos pela visibilidade do usuário e injeta `noAccessIcon`
 * nos itens em upsell (para o NavItem renderizar acinzentado).
 * @param {Array} groups  GESTAO_GROUPS ou CLIENTE_GROUPS
 * @param {Object} access store.getters['parameters/show'] (mapa flag → boolean)
 * @param {boolean} isInternal usuário é interno AAWZ (libera itens internalOnly)
 * @param {Object} [opts] { currentUser } — usuário p/ gates dependentes de perfil (Tesouraria)
 * @returns {Array} grupos com itens visíveis (grupos vazios são removidos)
 */
export function visibleGroups(groups, access, isInternal = false, opts = {}) {
  const show = access || {}
  const currentUser = opts.currentUser || {}
  const decorate = (item) => {
    const { visible, noAccessIcon } = evaluateItem(item, {
      show,
      isInternal,
      currentUser,
    })
    if (!visible) return null
    return noAccessIcon ? { ...item, noAccessIcon: true } : item
  }
  return groups
    .map((group) => ({
      ...group,
      items: group.items.map(decorate).filter(Boolean),
    }))
    .filter((group) => group.items.length > 0)
}

/**
 * Diz se um item está ativo dada a rota atual — casa pela chave de módulo
 * (1º segmento), então 'comissoes.geral' fica ativo em 'comissoes.visao-geral'.
 * @param {string} itemRouteName
 * @param {string} currentRouteName
 * @returns {boolean}
 */
export function isActiveRoute(itemRouteName, currentRouteName) {
  const key = routeModuleKey(itemRouteName)
  if (!key || !currentRouteName) return false
  return key === routeModuleKey(currentRouteName)
}
